import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { formatApiError } from "../lib/errors";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import {
  Bot, Globe, QrCode as QrCodeIcon, Copy, ExternalLink, ShieldCheck,
  ShieldAlert, Loader2, CheckCircle2, EyeOff,
} from "lucide-react";

/**
 * AssistantSection — v1.0.1 "Resources & AI Assistant" patch.
 *
 * Surfaces the primary AI Assistant for the current tenant directly inside
 * the Resources page so users see name, ID, status, public link, QR, and an
 * active/inactive toggle in one place. Backend-already-exists:
 *   - GET /api/assistants                  → list (we pick is_primary)
 *   - PATCH /api/assistants/{id}           → flip lifecycle_state
 *   - existing /chat/{slug} public route
 *
 * Out of scope: creating/editing other assistants — that's the future
 * multi-assistant management UI. This component is read-mostly for the
 * primary; the only mutation is the active/inactive toggle.
 */
export default function AssistantSection() {
  const [assistant, setAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const load = () =>
    api.get("/assistants")
      .then((r) => {
        const list = r.data?.assistants || [];
        setAssistant(list.find((a) => a.is_primary) || list[0] || null);
      })
      .catch(() => setAssistant(null))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <Card className="p-5 mb-6" data-testid="assistant-section-loading">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading AI Assistant…
        </div>
      </Card>
    );
  }
  if (!assistant) {
    // Legacy tenants without an assistants row yet (mid-migration) — render nothing.
    return null;
  }

  const isActive = (assistant.lifecycle_state || "").toLowerCase() === "active";
  const publicLink = `${window.location.origin}/chat/${assistant.slug}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(publicLink)}`;

  async function flipActive(targetActive) {
    setUpdating(true);
    try {
      await api.patch(`/assistants/${assistant.id}`, {
        lifecycle_state: targetActive ? "active" : "disabled",
        // Activating the assistant must also flip the public-facing flag on,
        // otherwise the toggle is a no-op for the public link (the /public/
        // chatbot route stays gated and returns 403). Deactivating turns it off.
        public_visibility: !!targetActive,
      });
      await load();
      toast.success(targetActive ? "Assistant activated" : "Assistant deactivated");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to update assistant"));
    } finally {
      setUpdating(false);
      setConfirmOpen(false);
    }
  }

  function copyId() {
    navigator.clipboard?.writeText(assistant.id);
    toast.success("Assistant ID copied");
  }
  function copyLink() {
    navigator.clipboard?.writeText(publicLink);
    toast.success("Public link copied");
  }

  return (
    <Card className="p-5 mb-6 border-emerald-100" data-testid="assistant-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* Left: assistant identity */}
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 grid place-items-center text-emerald-700 shrink-0">
            <Bot className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-lg font-semibold truncate max-w-xs" data-testid="assistant-name">
                {assistant.assistant_name}
              </h2>
              {isActive ? (
                <Badge
                  variant="outline"
                  className="bg-emerald-50 border-emerald-300 text-emerald-700 text-[10px] uppercase tracking-wider"
                  data-testid="assistant-status-pill"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block mr-1.5" />
                  Active
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="bg-rose-50 border-rose-300 text-rose-700 text-[10px] uppercase tracking-wider"
                  data-testid="assistant-status-pill"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 inline-block mr-1.5" />
                  Inactive
                </Badge>
              )}
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {assistant.assistant_role || "general"} · Primary
              </span>
            </div>
            <button
              type="button"
              onClick={copyId}
              className="mt-1 text-[11px] font-mono text-muted-foreground hover:text-emerald-700 inline-flex items-center gap-1 group"
              data-testid="assistant-id-copy"
              title="Click to copy"
            >
              <span className="bg-muted/50 px-1.5 py-0.5 rounded">ID: {assistant.id}</span>
              <Copy className="w-3 h-3 opacity-60 group-hover:opacity-100" />
            </button>
          </div>
        </div>

        {/* Right: active/inactive toggle */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {isActive ? "Public-facing" : "Disabled"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {isActive
                ? "Public link is live"
                : "Public link is disabled — visitors will see a 'not available' page"}
            </div>
          </div>
          <Switch
            checked={isActive}
            disabled={updating}
            onCheckedChange={(v) => {
              if (!v) {
                setConfirmOpen(true);
              } else {
                flipActive(true);
              }
            }}
            data-testid="assistant-active-toggle"
            className="data-[state=checked]:bg-emerald-600"
          />
        </div>
      </div>

      {/* Public link + QR row */}
      <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-4 items-stretch">
        <div className="border rounded-lg p-3 bg-background/60">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> Public assistant link
          </div>
          <div
            className={`font-mono text-xs break-all px-2 py-2 rounded ${
              isActive ? "bg-emerald-50/40 text-emerald-900" : "bg-muted text-muted-foreground line-through decoration-rose-400"
            }`}
            data-testid="assistant-public-link"
          >
            {publicLink}
          </div>
          <div className="flex flex-wrap gap-2 mt-2.5">
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="assistant-link-copy">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
            </Button>
            {isActive ? (
              <a href={publicLink} target="_blank" rel="noreferrer">
                <Button size="sm" variant="default" data-testid="assistant-link-open">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open Assistant
                </Button>
              </a>
            ) : (
              <Button size="sm" variant="default" disabled data-testid="assistant-link-open">
                <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Disabled
              </Button>
            )}
          </div>
          {!isActive && (
            <div className="mt-2 text-[11px] text-rose-700 flex items-start gap-1.5">
              <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
              Reactivate to bring the public link back online. Resource mappings are preserved.
            </div>
          )}
        </div>

        <div
          className={`border rounded-lg p-3 bg-background/60 grid place-items-center min-w-[140px] ${
            !isActive ? "opacity-50" : ""
          }`}
          data-testid="assistant-qr-card"
        >
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <QrCodeIcon className="w-3 h-3" /> QR
          </div>
          <img
            src={qrSrc}
            alt="Assistant public link QR"
            className="rounded"
            width={120}
            height={120}
            data-testid="assistant-qr-image"
          />
        </div>
      </div>

      {/* Deactivate confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="assistant-deactivate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-rose-600" />
              Deactivate AI Assistant?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Visitors hitting <span className="font-mono">{publicLink}</span> will see an
              "assistant unavailable" page until you reactivate. Your resources and mappings
              stay intact — only the public link goes offline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assistant-deactivate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => flipActive(false)}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="assistant-deactivate-confirm-btn"
            >
              {updating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
